import { getInvoicesNotPaid } from "@/api/invoices"
import { checkEmployeeHierarchy } from "@/api/utils"
import useDashboardFunc from "@/hooks/useDashboard"
import { AssignmentProps } from "@/interfaces/assignment_types"
import { BaseDashboardLayoutProps } from "@/interfaces/dashboard_types"
import { InvoiceProps } from "@/interfaces/invoices_types"
import { ProjectProps } from "@/interfaces/project_types"
import { RFIProps } from "@/interfaces/rfi_types"
import { SubmittalProps } from "@/interfaces/submittal_types"
import { TaskProps } from "@/interfaces/tasks_types"
import { useState, useMemo, useEffect } from "react"
import { AssignedDashboardContainer } from "./AssignedDashboard"
import { BidProps } from "@/interfaces/bid_types"

/**
 * A small component for the Main Menu component
 *
 * Shows the user's name and role, along with tasks and projects
 *
 * Dashboard will show different panels depending on the user's role
 *
 * @param user The user to display the dashboard for
 * @param announcements The announcements to display
 * @param contactUs The contact us to display
 */


export function BaseDashboardLayout(
    { user, announcements, contactUs }: BaseDashboardLayoutProps
) {
    const { isNewCard, markAsSeen } = useDashboardFunc()
    const [invoices, setInvoices] = useState<InvoiceProps[]>([])

    // Sorts all the Cards by those that are new, then by the date
    // Using memo to make it only render once

    // TODO: 
    // WE NEED TO SORT ALL THIS IN THE BACKEND WHY ARE WE DOING IT HERE?
    
    const sort_tasks = useMemo(() => {
        return (tasks: TaskProps[]) => {
            return [...tasks].sort((a, b) => {
                // First, sort by pinned status
                if (a.pinged && !b.pinged) return -1
                if (!a.pinged && b.pinged) return 1

                // Then, sort by new status
                const aIsNew = isNewCard('task', a.task_id ?? '')
                const bIsNew = isNewCard('task', b.task_id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1

                // Then, sort by due date
                return a.due_date.localeCompare(b.due_date) ?? 0
            })
        }
    }, [isNewCard])

    const sort_projects = useMemo(() => {
        return (projects: ProjectProps[]) => {
            return [...projects].sort((a, b) => {
                const aIsNew = isNewCard('project', a.project_id ?? '')
                const bIsNew = isNewCard('project', b.project_id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                return (a.end_date ?? '').localeCompare(b.end_date ?? '') ?? 0
            })
        }
    }, [])

    const sort_submittals = useMemo(() => {
        return (submittals: SubmittalProps[]) => {
            return [...submittals].sort((a, b) => {
                // First, sort by alert_status
                if (a.alert_status && !b.alert_status) return -1
                if (!a.alert_status && b.alert_status) return 1

                // Then, sort by new status
                const aIsNew = isNewCard('submittal', a.submittal_id ?? '')
                const bIsNew = isNewCard('submittal', b.submittal_id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                if (a.status !== b.status) {
                    const statusOrder = ['OPEN', 'CLOSED']
                    return statusOrder.indexOf(a.status) - statusOrder.indexOf(b.status)
                }
                return a.received_date.localeCompare(b.received_date) ?? 0
            })
        }
    }, [])

    const sort_rfis = useMemo(() => {
        return (rfis: RFIProps[]) => {
            return [...rfis].sort((a, b) => {
                // First, sort by alert_status
                if (a.alert_status && !b.alert_status) return -1
                if (!a.alert_status && b.alert_status) return 1

                // Then, sort by new status
                const aIsNew = isNewCard('rfi', a.RFI_id ?? '')
                const bIsNew = isNewCard('rfi', b.RFI_id ?? '')

                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                return a.date_received.localeCompare(b.date_received) ?? 0
            })
        }
    }, [])

    const sort_assignments = useMemo(() => {
        return (assignments: AssignmentProps[]) => {
            return [...assignments].sort((a, b) => {
                const aIsNew = isNewCard('assignment', a.id ?? '')
                const bIsNew = isNewCard('assignment', b.id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                return a.due_date?.localeCompare(b.due_date ?? '') ?? 0
            })
        }
    }, [])

    const sort_invoices = useMemo(() => {
        return (invoices: InvoiceProps[]) => {
            return [...invoices].sort((a, b) => {
                const aIsNew = isNewCard('invoice', a.invoice_id ?? '')
                const bIsNew = isNewCard('invoice', b.invoice_id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                return a.invoice_date.localeCompare(b.invoice_date) ?? 0
            })
        }
    }, [])

    const sort_bids = useMemo(() => {
        return (bids: BidProps[]) => {
            return [...bids].sort((a, b) => {
                const aIsNew = isNewCard('bid', a.bid_id ?? '')
                const bIsNew = isNewCard('bid', b.bid_id ?? '')
                if (aIsNew && !bIsNew) return -1
                if (!aIsNew && bIsNew) return 1
                if (a.start_date && !b.start_date) return -1
                if (!a.start_date && b.start_date) return 1
                return a.start_date?.localeCompare(b.start_date ?? '') ?? 0
            })
        }
    }, [isNewCard])

    // Get the invoices that are not paid (ONLY ACTIVATED FOR ACCOUNTANTS)
    useEffect(() => {
        const get_invoices = async () => {
            try {
                const response = await getInvoicesNotPaid()
                setInvoices(response.data ?? [])
            } catch (error) {
                console.warn("Error getting invoices", error)
            }
        }

        const roleHierarchy = checkEmployeeHierarchy(user.role)

        if (roleHierarchy === "FINANCE" || roleHierarchy === "ADMIN") {
            get_invoices()
        }
    }, [])

    const sorted_tasks = useMemo(() => sort_tasks(user.tasks ?? []), [user.tasks, sort_tasks])
    const sorted_projects = useMemo(() => sort_projects(user.projects ?? []), [user.projects, sort_projects])
    const sorted_submittals = useMemo(() => sort_submittals(user.submittals?.filter((submittal) => submittal.status !== 'Closed') ?? []), [user.submittals, sort_submittals])
    const sorted_rfis = useMemo(() => sort_rfis(user.RFIs?.filter((rfi) => rfi.status !== 'Closed') ?? []), [user.RFIs, sort_rfis])
    const sorted_invoices = useMemo(() => sort_invoices(invoices), [invoices, sort_invoices])
    const sorted_assignments = useMemo(() => sort_assignments(user.assignments ?? []), [user.assignments, sort_assignments])
    const sorted_bids = useMemo(() => sort_bids(user.bids ?? []), [user.bids, sort_bids])

    return (
        <div className="flex flex-col w-full dark:bg-stone-800">
            <AssignedDashboardContainer
                announcements={announcements}
                sorted_tasks={sorted_tasks}
                sorted_bids={sorted_bids}
                sorted_projects={sorted_projects}
                sorted_submittals={sorted_submittals}
                sorted_rfis={sorted_rfis}
                sorted_assignments={sorted_assignments}
                isNewCard={isNewCard}
                markAsSeen={markAsSeen}
                contactUs={contactUs}
                role={user.role}
                sorted_invoices={sorted_invoices} />
        </div>
    )
}
